<script setup lang="ts">
import CheckboxInput from "~/components/Common/Inputs/CheckboxInput.vue";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { storeToRefs } from "pinia";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import AreaInput from "~/components/Common/Inputs/AreaInput.vue";
import { UserLanguageEnum } from "~/utils/enums/users.enums";
import CommonInput from "~/components/Common/Inputs/CommonInput.vue";
import type { IHabilitation } from "~/utils/interfaces/habilitations-interfaces";
import { useUsersHabilitationsStore } from "~/store/users-habilitations/users-habilitations.store";
import Editor from "~/components/Common/Rich-text-editor/Editor.vue";
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { HabilitationsApi } from "@simbelapp/order-sdk";
import AiFieldMarker from "~/components/Pages/company-catalog/training-modal/prefill/AiFieldMarker.vue";
import AiFilledField from "~/components/Pages/company-catalog/training-modal/prefill/AiFilledField.vue";
import { useTrainingPrefillStore } from "~/store/copilot/training-prefill.store";
import { TrainingPrefillFieldEnum } from "~/utils/enums/training-prefill.enums";

const { t } = useI18n();

const createTraining = useCreateTrainingStore();
const prefillStore = useTrainingPrefillStore();
const { training, readonly } = storeToRefs(createTraining);

const habilitationStore = useUsersHabilitationsStore();

const newHabilitationName = ref("");
const goalInputRefs = ref<any[]>([]);

async function pushNewGoal() {
  training.value.content.goals.push("");
  await nextTick();
  const lastRef = goalInputRefs.value[training.value.content.goals.length - 1];
  lastRef?.$el?.querySelector("input")?.focus();
}

function removeGoal(index: number) {
  training.value.content.goals.splice(index, 1);
}

const languageOpts = Object.values(UserLanguageEnum);
const placeholderGoals = computed(() => {
  if (training.value.content.goals?.length > 1) return `Nouvel objectif`;
  return `Décrivez le premier objectif de la formation`;
});

const programValue = computed({
  get: () => training.value.content.program ?? undefined,
  set: (value) => {
    training.value.content.program = value ?? null;
  },
});

const isHTMLContent = (content: string): boolean => {
  if (!content) return false;
  const htmlRegex = /<[^>]*>/;
  return htmlRegex.test(content);
};

const shouldUseAreaInput = computed(() => {
  return readonly.value && !isHTMLContent(training.value.content.program || "");
});

function updateHabilitationOpt(habilitation: IHabilitation) {
  if (habilitation) {
    training.value.content.durationYears = habilitation?.durationYears;
  } else {
    training.value.content.durationYears = null;
  }
}

function createNewHabilitation() {
  training.value.content.habilitation = {
    habilitationId: null,
    name: newHabilitationName.value,
    durationYears: training.value.content.durationYears ?? 1,
    expirationDate: null,
    searchFields: null,
  };
}

function updateGoals(index: number) {
  if (training.value.content.goals[index] === "" && training.value.content.goals.length > 1) {
    training.value.content.goals.splice(index, 1);
  }
}
onMounted(async () => {
  training.value.content.goals = training.value.content.goals.filter((goal) => goal !== "");

  if (!training.value.content.goals.length) {
    training.value.content.goals.push("");
  }

  await habilitationStore.fetchAvailableHabilitations();
});

const servicesAPI = async (params: any) => {
  const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
  const response = await apiInstance.findAvailableHabilitations({
    lazyLoad: true,
    ...params,
  });

  if (response) {
    return response;
  }
};
</script>

<template>
  <h3 class="heading_3_demi">Contenu</h3>
  <div class="training__step_container">
    <div class="fields">
      <div class="grid">
        <label class="text-secondary-color !flex flex-row items-center gap-2">
          Objectifs
          <AiFieldMarker v-if="prefillStore.isPrefilled(TrainingPrefillFieldEnum.GOALS)" />
        </label>
        <AiFilledField :active="prefillStore.isPrefilled(TrainingPrefillFieldEnum.GOALS)">
          <div v-if="training.content.goals.length" class="grid gap-[8px] pb-[8px]">
            <div v-for="(goal, index) in training.content.goals" :key="index" class="flex items-center gap-[8px]">
              <CommonInput
                :ref="(el) => (goalInputRefs[index] = el)"
                v-model="training.content.goals[index]"
                style="text-align: start"
                :placeholder="placeholderGoals"
                :error="!!training.errors['content.goals']"
                :disabled="readonly"
                @keyup.enter.stop="pushNewGoal"
                @update:model-value="updateGoals(index)"
              />
              <SvgIcon
                v-if="!readonly && training.content.goals.length > 1"
                style="width: 16px; height: 16px"
                class="cursor-pointer text-secondary-color"
                name="close"
                @click="removeGoal(index)"
              ></SvgIcon>
            </div>
          </div>
        </AiFilledField>
        <div
          class="flex items-center gap-[8px] justify-center cursor-pointer button_secondary_default"
          @click="pushNewGoal"
        >
          <SvgIcon style="width: 16px; height: 16px" name="add"></SvgIcon> <span>Ajouter un objectif</span>
        </div>
      </div>
      <div>
        <label class="text-secondary-color !flex flex-row items-center gap-2">
          Public ciblé
          <AiFieldMarker v-if="prefillStore.isPrefilled(TrainingPrefillFieldEnum.TARGET_AUDIENCE)" />
        </label>
        <AiFilledField :active="prefillStore.isPrefilled(TrainingPrefillFieldEnum.TARGET_AUDIENCE)">
          <CommonInput
            v-model="training.content.targetAudience"
            style="text-align: start"
            placeholder="Public visé par la formation"
            :error="!!training.errors['content.targetAudience']"
            :disabled="readonly"
          />
        </AiFilledField>
      </div>
      <div>
        <label class="text-secondary-color !flex flex-row items-center gap-2">
          Programme
          <AiFieldMarker v-if="prefillStore.isPrefilled(TrainingPrefillFieldEnum.PROGRAM)" />
        </label>

        <AiFilledField :active="prefillStore.isPrefilled(TrainingPrefillFieldEnum.PROGRAM)">
          <AreaInput
            v-if="shouldUseAreaInput"
            v-model="training.content.program"
            :disabled="readonly"
            class="textarea__input"
            placeholder="Décrivez le programme de la formation"
          />

          <Editor
            v-else
            v-model="programValue"
            :disabled="readonly"
            placeholder="Décrivez le programme de la formation..."
            :actions-list="[
              'default',
              'list',
              'blockquote',
              'separator',
              'justify',
              'text-style',
              'color-text',
              'link',
              // 'table',
              // 'image'
            ]"
          />
        </AiFilledField>
      </div>
      <div class="flex flex-col">
        <label class="text-secondary-color">Langue*</label>
        <SimpleDropdown
          v-model="training.content.language"
          :disabled="readonly"
          :options="languageOpts"
          :error="!!training.errors['content.language']"
        >
          <template #singleLabel="{ option }">
            <span>{{ t(option) }}</span>
          </template>
          <template #option="{ option }">
            <span>{{ t(option) }}</span>
          </template>
        </SimpleDropdown>
        <span v-if="!!training.errors['content.language']" class="text-primary-red-color mt-2">
          {{ training.errors["content.language"] }}
        </span>
      </div>
    </div>
    <label
      class="!flex flex-row gap-2 !mb-[16px] items-center w-fit"
      :class="readonly ? 'cursor-not-allowed' : 'cursor-pointer'"
    >
      <CheckboxInput
        v-model="training.content.isHabilitation"
        :disabled="readonly"
        :checked="training.content.isHabilitation"
      ></CheckboxInput>
      <span class="text-secondary-color">La formation délivre une habilitation</span>
    </label>
    <div v-if="training.content.isHabilitation" class="flex flex-row gap-[8px]">
      <div class="flex-1">
        <span class="text-secondary-color">Habilitation*</span>
        <SimpleDropdown
          v-model="training.content.habilitation"
          :disabled="readonly"
          :options="habilitationStore.habilitationsList || []"
          :loading="!habilitationStore.habilitationsList"
          :searchable="true"
          :store="habilitationStore"
          store-object="habilitationsList"
          label="name"
          track-by="habilitationId"
          :services-a-p-i="servicesAPI"
          :add="!!newHabilitationName.length"
          :delete="!readonly"
          placeholder="Nom de l'habilitation"
          :error="!!training.errors['content.habilitation']"
          @search-value="(value: string) => (newHabilitationName = value)"
          @update:model-value="updateHabilitationOpt"
          @click-add="createNewHabilitation"
        >
          <template #add>Ajouter `{{ newHabilitationName }}`</template>
          <template #option="{ option }">
            <span>{{ option.name }}</span>
          </template>
        </SimpleDropdown>
        <span v-if="!!training.errors['content.habilitation']" class="text-primary-red-color">
          {{ training.errors["content.habilitation"] }}
        </span>
      </div>
      <div class="max-w-[126px]">
        <span class="text-secondary-color">Durée de validité*</span>
        <span class="flex items-center gap-[8px]">
          <CommonInput
            v-model="training.content.durationYears"
            :disabled="readonly"
            style="max-width: 88px; width: 88px"
            placeholder="-"
            type="number"
            :error="!!training.errors['content.durationYears']"
          ></CommonInput>
          <span class="text-tertiary-color">an(s)</span>
        </span>
        <span v-if="!!training.errors['content.durationYears']" class="text-primary-red-color">
          {{ training.errors["content.durationYears"] }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.training__step_container {
  @apply mt-4 px-4;

  label {
    @apply block mb-[4px];
  }
}

.fields {
  @apply flex flex-col mb-8 gap-2;
}
</style>
